import { useEffect, useRef, useState } from 'react';
import { intakePlan, intakeRequest, prepareIntakeFiles, categoryLabels, intakeReviewValueError } from '../../utils/clientDocumentIntake';

export default function useClientDocumentIntake({ open, draft, setDraft, existingKyc = {}, reviewExisting = false, separateNames = false }) {
  const [documents, setDocuments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [unidentified, setUnidentified] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [phoneRevision, setPhoneRevision] = useState(0);
  const [kyc, setKyc] = useState({});
  const [filledValues, setFilledValues] = useState({});
  const [cv, setCv] = useState(null);
  const latest = useRef({});
  const controller = useRef(null);
  const generation = useRef(0);
  const locked = useRef(false);
  latest.current = { draft, documents, review, kyc, cv, unidentified, existingKyc, reviewExisting, separateNames };
  useEffect(() => {
    generation.current++;
    controller.current?.abort(); locked.current = false;
    setDocuments([]); setFilledValues({}); setUnidentified(false); setBusy(false); setMessage(''); setReview(null); setReviewOpen(false); setKyc({}); setCv(null);
    return () => { generation.current++; controller.current?.abort(); };
  }, [open]);

  // Only expose CV information once any document conflicts have been resolved.
  // A replacement CV resets suggestions so old selections are not carried over.
  useEffect(() => {
    const doc = [...documents].reverse().find(d => d.category === 'cv' && d.status === 'extracted' && d.ready);
    setCv(prev => {
      if (!doc?.extraction?.cvProfile) return null;
      if (prev?.documentId === doc.id) return prev;
      return { documentId: doc.id, ...doc.extraction.cvProfile, selectedSources: [] };
    });
  }, [documents]);

  async function run(input, retry = false) {
    if (locked.current || latest.current.review || latest.current.unidentified) return;
    locked.current = true; setBusy(true); setMessage('Reading document...');
    const runId = generation.current;
    const alive = () => runId === generation.current;
    let prepared; let sent = false;
    try {
      prepared = retry ? input : await prepareIntakeFiles(input);
      if (!alive()) return;
      const existing = latest.current.documents;
      if (!retry) prepared = prepared.filter(f => !existing.some(d => d.file.dataBase64 === f.dataBase64));
      if (!prepared.length) { setMessage('This document has already been added.'); return; }
      if (!retry && (existing.length + prepared.length > 5 || existing.reduce((n, d) => n + d.file.size, 0) + prepared.reduce((n, f) => n + f.size, 0) > 20 * 1024 * 1024)) throw new Error('Use up to 5 documents, totalling no more than 20 MB.');
      controller.current = new AbortController();
      const context = Object.fromEntries(['givenNames','familyName','clientName','clientType','address','city','country','contactPhone','contactEmail'].map(k => [k, latest.current.draft[k] || '']));
      Object.assign(context, latest.current.existingKyc, Object.fromEntries(Object.entries(latest.current.kyc).map(([k,v]) => [k,v.value])));
      sent = true;
      const result = await intakeRequest('extract', { files: prepared, context }, controller.current.signal, progress => {
        if (alive()) setMessage(progress.stage === 'extracting' && categoryLabels[progress.category]
          ? `Extracting data from ${progress.category === 'cv' ? 'CV' : progress.category === 'id_document' ? 'ID' : categoryLabels[progress.category].toLowerCase()}...`
          : 'Reading document...');
      });
      if (!alive()) return;
      applyExtraction({ prepared, result });
    } catch (e) {
      if (!alive() || e.name === 'AbortError') return;
      setMessage(e.message);
      if (sent && prepared?.length && !retry) setDocuments(prev => [...prev, ...prepared.filter(f => !prev.some(d => d.id === f.id)).map(file => ({ id: file.id, name: file.name, file, status: 'error', error: { message: e.message }, ready: false }))]);
    } finally {
      if (alive()) { locked.current = false; setBusy(false); }
    }
  }
  function applyExtraction({ prepared, result }) {
      // Re-evaluate against the current form, not the form at request start.
      const now = latest.current;
      const current = { ...now.draft, ...now.existingKyc, ...Object.fromEntries(Object.entries(now.kyc).map(([k,v]) => [k,v.value])) };
      const isUnidentified = d => d.status === 'not_identified' || d.category === 'not_identified';
      setUnidentified(result.documents.some(isUnidentified));
      const docs = result.documents.filter(d => d.status !== 'duplicate' && !isUnidentified(d)).map(d => ({ ...d, file: prepared.find(f => f.id === d.id), ready: false }));
      const plan = intakePlan(docs, current, now.documents, { reviewExisting: now.reviewExisting, separateNames: now.separateNames });
      setFilledValues(prev => ({ ...prev, ...plan.autoFill, ...Object.fromEntries(Object.entries(plan.autoKyc).map(([field, item]) => [field, item.value])) }));
      setDraft(prev => ({ ...prev, ...plan.autoFill }));
      setKyc(prev => ({ ...prev, ...plan.autoKyc }));
      if (plan.autoFill.contactPhone !== undefined) setPhoneRevision(v => v + 1);
      setDocuments(prev => [...prev.filter(d => !prepared.some(f => f.id === d.id)), ...docs.map(d => ({ ...d, ready: d.status === 'extracted' && !plan.review.length }))]);
      if (plan.review.length) {
        setReview({ ...plan, previousKyc: now.kyc, documents: docs.filter(d => d.status === 'extracted') });
        setReviewOpen(true);
      }
      setMessage(docs.some(d => d.status !== 'extracted') ? 'Some documents could not be read. Retry or remove them below.' : '');
  }
  function resolveReview(includedIds, choices) {
    if (!review) return;
    const allowed = new Set(includedIds);
    const clientPatch = {}; const kycPatch = {};
    review.review.forEach(row => {
      const choice = choices[row.field];
      const manual = choice && typeof choice === 'object' && allowed.has(choice.documentId)
        && [...row.options, ...row.issues].some(o => o.documentId === choice.documentId)
        && !intakeReviewValueError(row.field, choice.value);
      const option = manual ? { documentId: choice.documentId, value: choice.value.trim() }
        : row.options.find(o => `${o.documentId}:${o.value}` === choice && allowed.has(o.documentId));
      if (!option) return;
      if (['dateOfBirth','nationality','incorporationDate'].includes(row.field)) kycPatch[row.field] = { value: option.value, documentId: option.documentId };
      else clientPatch[row.field] = option.value;
    });
    setFilledValues(prev => ({ ...prev, ...clientPatch, ...Object.fromEntries(Object.entries(kycPatch).map(([field, item]) => [field, item.value])) }));
    // Discarding a reviewed document also discards its automatic additions,
    // unless another retained document supplied the same value or the user edited it.
    setDraft(prev => {
      const next = { ...prev };
      for (const [field, sources] of Object.entries(review.autoSources || {})) {
        if (!sources.some(id => allowed.has(id)) && next[field] === review.autoFill[field]) next[field] = review.autoPrevious?.[field] || '';
      }
      return { ...next, ...clientPatch };
    });
    const reviewedIds = new Set(review.documents.map(d => d.id));
    setKyc(prev => {
      const next = { ...prev };
      for (const [field, value] of Object.entries(next)) {
        if (reviewedIds.has(value.documentId) && !allowed.has(value.documentId)) {
          if (review.previousKyc?.[field]) next[field] = review.previousKyc[field];
          else delete next[field];
        }
      }
      return { ...next, ...kycPatch };
    });
    setPhoneRevision(v => v + 1);
    const ids = new Set(review.documents.map(d => d.id));
    setDocuments(prev => prev.filter(d => !ids.has(d.id) || allowed.has(d.id)).map(d => ids.has(d.id) ? { ...d, ready: true } : d));
    setReview(null); setReviewOpen(false);
  }
  function remove(id) {
    setDocuments(prev => prev.filter(d => d.id !== id));
    setKyc(prev => Object.fromEntries(Object.entries(prev).filter(([,v]) => v.documentId !== id)));
  }
  function editKyc(field, value) {
    // Only ID-originated fields can be edited here; manual entry stays unchanged.
    setKyc(prev => prev[field] ? { ...prev, [field]: { ...prev[field], value } } : prev);
  }
  return { filledValues, unidentified, dismissUnidentified: () => setUnidentified(false), applyExtraction, phoneRevision, documents, busy, message, review, reviewOpen, kyc, cv, run, remove, resolveReview, editKyc,
    closeReview: () => setReviewOpen(false),
    openReview: () => setReviewOpen(true),
    editCvActivity: businessActivity => setCv(prev => prev ? { ...prev, businessActivity } : prev),
    toggleCvSource: category => setCv(prev => !prev || !prev.suggestions.some(s => s.category === category) ? prev : {
      ...prev, selectedSources: prev.selectedSources.includes(category)
        ? prev.selectedSources.filter(key => key !== category) : [...prev.selectedSources, category],
    }),
    cvForSave: () => latest.current.cv,

    hasUnresolved: documents.some(d => !d.ready),
    filesForSave: () => latest.current.documents.filter(d => d.ready).map(d => ({ ...d.file, category: d.category })),
    kycForSave: () => Object.fromEntries(Object.entries(latest.current.kyc).map(([k,v]) => [k,v.value])),
  };
}
